import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Autocomplete,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import wineService, {
  Wine,
  WineWineName,
  parseVintageInput,
  formatVintageInput,
} from '../../../services/wineService';

const WineForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);

  // WineName autocomplete
  const [wineNameOptions, setWineNameOptions] = useState<WineWineName[]>([]);
  const [wineNameInput, setWineNameInput] = useState('');
  const [selectedWineName, setSelectedWineName] = useState<WineWineName | null>(null);
  const [loadingWineNames, setLoadingWineNames] = useState(false);

  // Vintage input
  const [vintageInput, setVintageInput] = useState('');

  // Other fields
  const [active, setActive] = useState(true);

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ wineName?: string; vintage?: string }>({});

  // Load existing wine in edit mode
  useEffect(() => {
    if (!isEditMode || !id) return;
    setLoading(true);
    wineService
      .getById(id)
      .then((wine) => {
        setSelectedWineName(wine.wineName);
        setWineNameInput(wine.wineName.displayName);
        setVintageInput(formatVintageInput(wine));
        setActive(wine.active);
      })
      .catch(() => console.error('Failed to fetch wine'))
      .finally(() => setLoading(false));
  }, [isEditMode, id]);

  // Search wine names
  useEffect(() => {
    if (wineNameInput.length < 2) {
      setWineNameOptions([]);
      return;
    }
    let active = true;
    setLoadingWineNames(true);
    wineService
      .searchWineNames(wineNameInput)
      .then((results) => {
        if (active) setWineNameOptions(results);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoadingWineNames(false);
      });
    return () => {
      active = false;
    };
  }, [wineNameInput]);

  const parsedVintage = parseVintageInput(vintageInput);

  const getWineNameLabel = useCallback((wn: WineWineName) => wn.displayName, []);

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!selectedWineName) newErrors.wineName = 'Wine name is required';
    if (!vintageInput.trim()) {
      newErrors.vintage = 'Vintage is required';
    } else if (!parsedVintage.valid) {
      newErrors.vintage = parsedVintage.error;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        wineNameId: selectedWineName!._id,
        vintageType: parsedVintage.vintageType,
        vintage: parsedVintage.vintage,
        baseVintage: parsedVintage.baseVintage,
        active,
      };

      if (isEditMode && id) {
        await wineService.update(id, payload);
        navigate('/admin/wines');
      } else {
        // Check duplicate before creating
        const dupCheck = await wineService.checkDuplicate({
          wineNameId: selectedWineName!._id,
          vintageType: parsedVintage.vintageType,
          vintage: parsedVintage.vintage,
        });
        if (dupCheck.isDuplicate) {
          setErrors({ vintage: 'A wine with this vintage already exists for this wine name' });
          setSaving(false);
          return;
        }
        await wineService.create(payload);
        navigate('/admin/wines');
      }
    } catch (error) {
      console.error('Failed to save wine:', error);
      alert('Failed to save wine. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Preview of generated wine name
  const previewName =
    selectedWineName && vintageInput.trim() && parsedVintage.valid
      ? `${selectedWineName.displayName} ${parsedVintage.displayVintage}`
      : null;

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Wine' : 'Add Wine'}
        </Typography>

        <form onSubmit={handleSubmit}>
          {/* Wine Name */}
          <Autocomplete
            sx={{ mt: 2 }}
            options={wineNameOptions}
            loading={loadingWineNames}
            value={selectedWineName}
            inputValue={wineNameInput}
            getOptionLabel={getWineNameLabel}
            isOptionEqualToValue={(opt, val) => opt._id === val._id}
            filterOptions={(x) => x}
            onInputChange={(_, value) => setWineNameInput(value)}
            onChange={(_, value) => {
              setSelectedWineName(value);
              setWineNameInput(value ? value.displayName : '');
              if (errors.wineName) setErrors((e) => ({ ...e, wineName: undefined }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Wine Name"
                required
                error={Boolean(errors.wineName)}
                helperText={errors.wineName || 'Search for a wine name record'}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingWineNames ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box>
                  <Typography variant="body2">{option.displayName}</Typography>
                  {option.producer && (
                    <Typography variant="caption" color="text.secondary">
                      {option.producer.fullName || option.producer.name}
                      {option.region ? ` · ${option.region.name}` : ''}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
          />

          {/* Vintage input */}
          <TextField
            fullWidth
            label="Vintage"
            value={vintageInput}
            onChange={(e) => {
              setVintageInput(e.target.value);
              if (errors.vintage) setErrors((err) => ({ ...err, vintage: undefined }));
            }}
            margin="normal"
            required
            error={Boolean(errors.vintage)}
            helperText={
              errors.vintage ||
              'Enter a year (e.g. 2018), "NV" for non-vintage, or "MV" for multi-vintage'
            }
            inputProps={{ maxLength: 4 }}
          />

          {/* Preview */}
          {previewName && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                px: 2,
                py: 1.5,
                borderRadius: 1,
                backgroundColor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5, fontWeight: 600, letterSpacing: 0.5 }}
              >
                GENERATED WINE NAME
              </Typography>
              <Typography variant="body2" color="text.primary">
                {previewName}
              </Typography>
              <Chip
                label={parsedVintage.vintageType.replace('_', ' ')}
                size="small"
                variant="outlined"
                sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
              />
            </Box>
          )}

          {/* Active */}
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Wine'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => navigate('/admin/wines')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default WineForm;
